'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { Button } from '@workspace/ui/components/button';
import { AlertDialog } from '@workspace/ui/components/custom/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { G2ReviewIframe } from '@workspace/ui/components/g2-review/g2-review-iframe';
import { NpsRating } from '@workspace/ui/components/g2-review/nps-rating';
import { PrivateFeedbackForm } from '@workspace/ui/components/g2-review/private-feedback-form';
import { useCallback, useState } from 'react';

export interface NpsFeedbackPayload {
  email: string;
  rating: number;
  response?: string;
  sentiment: 'detractor' | 'neutral' | 'promoter';
  survey_name: string;
  submitted_at: string;
}

type ModalStep = 'rating' | 'g2-review' | 'private-feedback' | 'thank-you';

interface NpsG2ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted: () => void;
  onDismissed: () => void;
  userEmail: string;
  getG2ReviewUrl: () => Promise<string>;
  submitFeedback: (data: NpsFeedbackPayload) => Promise<void>;
  feedbackLoadingText: string;
  surveyName?: string;
  npsThreshold?: number;
  /** Temporary flag — when true, all ratings go to private feedback (skips G2 iframe) */
  disableG2Review?: boolean;
}

function getSentiment(rating: number): 'detractor' | 'neutral' | 'promoter' {
  if (rating >= 9) return 'promoter';
  if (rating >= 7) return 'neutral';
  return 'detractor';
}

export function NpsG2Modal({
  isOpen,
  onClose,
  onCompleted,
  onDismissed,
  userEmail,
  getG2ReviewUrl,
  submitFeedback,
  feedbackLoadingText,
  surveyName = 'In-app NPS',
  npsThreshold,
  disableG2Review = false,
}: NpsG2ModalProps) {
  const [step, setStep] = useState<ModalStep>('rating');
  const [rating, setRating] = useState<number | null>(null);
  const [g2ReviewUrl, setG2ReviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingG2, setIsLoadingG2] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const threshold = npsThreshold ?? Number(process.env.NEXT_PUBLIC_G2_NPS_THRESHOLD ?? '9');

  const resetState = useCallback(() => {
    setStep('rating');
    setRating(null);
    setG2ReviewUrl(null);
    setIsSubmitting(false);
    setIsLoadingG2(false);
    setHasSubmitted(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        // Dialog is closing - determine if completed or dismissed
        if (hasSubmitted) {
          onCompleted();
        } else {
          onDismissed();
        }
        onClose();
        // Reset state after close animation
        setTimeout(resetState, 300);
      }
    },
    [hasSubmitted, onCompleted, onDismissed, onClose, resetState]
  );

  const handleRatingSubmit = useCallback(
    async (selectedRating: number) => {
      setRating(selectedRating);

      if (!disableG2Review && selectedRating >= threshold) {
        // Promoter — load G2 review form
        setIsLoadingG2(true);
        try {
          const url = await getG2ReviewUrl();
          setG2ReviewUrl(url);
          setStep('g2-review');
        } catch (error) {
          console.error('Failed to load G2 review URL:', error);
          // Fallback to private feedback if G2 fails
          setStep('private-feedback');
        } finally {
          setIsLoadingG2(false);
        }
      } else {
        // Detractor / neutral — private feedback
        setStep('private-feedback');
      }
    },
    [disableG2Review, threshold, getG2ReviewUrl]
  );

  const handlePrivateFeedbackSubmit = useCallback(
    async (feedback: string) => {
      if (rating === null) return;
      setIsSubmitting(true);
      try {
        await submitFeedback({
          email: userEmail,
          rating,
          response: feedback || undefined,
          sentiment: getSentiment(rating),
          survey_name: surveyName,
          submitted_at: new Date().toISOString(),
        });
        setHasSubmitted(true);
        setStep('thank-you');
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [rating, userEmail, surveyName, submitFeedback]
  );

  const handleG2ReviewComplete = useCallback(async () => {
    if (rating === null) return;
    setIsSubmitting(true);
    try {
      await submitFeedback({
        email: userEmail,
        rating,
        sentiment: getSentiment(rating),
        survey_name: surveyName,
        submitted_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Failed to submit feedback after G2 review:', error);
    } finally {
      setIsSubmitting(false);
      setHasSubmitted(true);
      setStep('thank-you');
    }
  }, [rating, userEmail, surveyName, submitFeedback]);

  // Show success AlertDialog when on thank-you step
  if (step === 'thank-you') {
    return (
      <AlertDialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        variant="success"
        title="Thank you!"
        description="We truly appreciate you taking the time to share your feedback. It helps us build a better platform for everyone."
      >
        <Button variant="primary" className="w-full" onClick={onClose}>
          Close
        </Button>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader className="sr-only">
          <DialogTitle>Share Your Feedback</DialogTitle>
          <DialogDescription>
            Help us improve by sharing your experience with our platform.
          </DialogDescription>
        </DialogHeader>

        <div className="w-full">
          {step === 'rating' && (
            <NpsRating
              onSubmit={handleRatingSubmit}
              isSubmitting={isLoadingG2}
              loadingText={feedbackLoadingText}
            />
          )}

          {step === 'g2-review' && g2ReviewUrl && (
            <G2ReviewIframe reviewUrl={g2ReviewUrl} onComplete={handleG2ReviewComplete} />
          )}

          {step === 'private-feedback' && rating !== null && (
            <PrivateFeedbackForm
              rating={rating}
              onSubmit={handlePrivateFeedbackSubmit}
              isSubmitting={isSubmitting}
              loadingText={feedbackLoadingText}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
